import {ENDPOINT_ORGANIZATION, ENDPOINT_ORGANIZATION_V3} from 'common/endpoints';
import _ from 'lodash';
import {checkIfDefinedNonNullAndNotEmptyString} from 'common/utils';
import goStore from 'go/store';
import {getInfo} from 'common/storeUtils';
import {commonJwtHeader} from 'common/utils';
import {forceJsonResponse} from 'common/utils';
import {FLUX} from 'app-ui-componentlibrary/dist/common/constants';

// get organization by organization id
const getOrganization = (token, orgId) => {
  return fetch(`${ENDPOINT_ORGANIZATION}${orgId}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

// get organization by organization state
const getOrganizationsByState = (token, state) => {
  return fetch(`${ENDPOINT_ORGANIZATION}?state=${state}`, {
    method: 'POST',
    headers: commonJwtHeader(),
  });
};

const getOrganizationsByFuzzyUEIAndState = ({value, state, returnRawResponse}) => {
  return fetch(`${ENDPOINT_ORGANIZATION}?uei=${value}&state=${state}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  })
    .then((res) => forceJsonResponse(res, FLUX))
    .then((res) => {
      if (returnRawResponse) {
        return new Response(JSON.stringify(res));
      }
      if (res) {
        const orgData = res.map((item) => {
          const eft = !_.isEmpty(_.trim(item.eft))
            ? _.trim(item.eft)
            : _.trim(item.dunsPlus4);
          const ueiPlusEft = item.uei + (!_.isEmpty(eft) ? '-' + eft : '');
          const uei = item.uei ? ` | UEI-EFT: ${ueiPlusEft}` : '';
          const getName = item.displayName + uei;

          return {
            legalName: getName,
            displayName: getName,
            organizationId: '' + item.organizationId,
            entityId: item.organizationId,
            isOrg: false,
            orgDisplayName: item.displayName,
            orgEntityId: '' + item.organizationId,
          };
        });
        return new Response(JSON.stringify(orgData));
      }
    });
};

const getSubOrganization = (token, orgId, subOrgId) => {
  const endPointToUse = ENDPOINT_ORGANIZATION + `${orgId}/suborganizations/${subOrgId}`;

  return fetch(endPointToUse, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const updateSubOrganization = (token, parentOrgId, subOrgId, payload) => {
  return fetch(`${ENDPOINT_ORGANIZATION}${parentOrgId}/suborganizations/${subOrgId}`, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const getMySubOrganizations = (token, foId, programId, foRestricted) => {
  let queryString = '';
  if (foId) {
    queryString = `?fundingOpportunityId=${foId}${
      programId ? `&programId=${programId}` : ''
    }${foRestricted ? `&foRestricted=${foRestricted}` : ''}`;
  } else if (programId) {
    queryString = `?programId=${programId}`;
  }

  return fetch(`${ENDPOINT_ORGANIZATION}user/suborganizations${queryString}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getSubOrganizations = ({token, orgId}) => {
  const methodToUse = 'GET';
  return fetch(`${ENDPOINT_ORGANIZATION}${orgId}/suborganizations`, {
    method: methodToUse,
    headers: commonJwtHeader(),
  });
};

const getOrganizationByDUNSFromSAMGOV = (token, dunsNumber) => {
  return fetch(`${ENDPOINT_ORGANIZATION}samgov/${dunsNumber}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getOrganizationByUEIFromSAMGOV = (token, ueiNumber) => {
  const ueiNumberTrimmed = ueiNumber.replace('-', '');
  return fetch(`${ENDPOINT_ORGANIZATION}samgov/uei/${ueiNumberTrimmed}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

function isArrayOfNumbers(value) {
  return _.isArray(value) && _.every(value, _.isNumber);
}

const callGetOrganizationsListById = ({token, orgIds}) => {
  const orgIdsAsArray = isArrayOfNumbers(orgIds)
    ? orgIds
    : Object.values(_.omit(orgIds, 'loading'));

  return getOrganizationsListById(token, orgIdsAsArray);
};

const getOrganizationsListById = (token, orgIds) => {
  const orgIdsStr = _.isArray(orgIds) ? orgIds.join(',') : orgIds;
  return fetch(`${ENDPOINT_ORGANIZATION}?organizationIds=${orgIdsStr}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getSearchForSuborganizations = (
  token,
  organizationId,
  programIds,
  searchFilter,
  offset,
  limit = 1000
) => {
  const {duns, uei, name, ein, address, sortBy, sortOrder} = searchFilter || {};
  const queryPayload = {
    dunsNumber: duns,
    uei: _.size(uei) > 12 ? uei.substring(0, 12) : uei,
    eft: _.size(uei) > 12 ? _.replace(uei, '-', '').substring(12) : undefined,
    legalBusinessName: name,
    ein: ein,
    address: address,
    sortBy: sortBy,
    sortOrder: sortOrder,
    limit,
    offset,
  };
  return fetch(
    `${ENDPOINT_ORGANIZATION}${organizationId}/suborganizations/search/${programIds}`,
    {
      method: 'POST',
      headers: commonJwtHeader(),
      body: JSON.stringify(queryPayload),
    }
  );
};

/**
 * Not used
 * Not migrating to refactored
 */
const postOrgContact = (token, organizationId, payload) => {
  const methodToUse = 'PUT';

  return fetch(`${ENDPOINT_ORGANIZATION}${organizationId}/contact`, {
    method: methodToUse,
    dataType: JSON,
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const getRiskAssessentByOrgId = (token, programId, organizationId) => {
  const endPointToUse =
    ENDPOINT_ORGANIZATION +
    `risks?organizationId=${organizationId}&programId=${programId}`;

  return fetch(endPointToUse, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getRecipientsByFuzzySearch = (searchStr) => {
  return fetch(`${ENDPOINT_ORGANIZATION}recipientSearch?searchString=${searchStr}`, {
    method: 'GET',
    dataType: JSON,
    headers: commonJwtHeader(),
  });
};

const getSubOrganizationsByOrgIdAndProgramIds = ({
  token,
  orgId,
  programIds,
  offset,
  limit,
}) => {
  return getSearchForSuborganizations(token, orgId, programIds, null, offset, limit);
};

export default {
  getOrganization,
  getSubOrganizations,
  getMySubOrganizations,
  getOrganizationByDUNSFromSAMGOV,
  getOrganizationByUEIFromSAMGOV,
  getOrganizationsListById,
  getOrganizationsByState,
  getSearchForSuborganizations,
  updateSubOrganization,
  postOrgContact,
  getRiskAssessentByOrgId,
  getSubOrganization,
  callGetOrganizationsListById,
  getRecipientsByFuzzySearch,
  getSubOrganizationsByOrgIdAndProgramIds,
  getOrganizationsByFuzzyUEIAndState,
};
